/**
 * External file links API — typed wrappers over /api/external-links/* and the
 * per-card-file convenience routes (migration 0074).
 *
 * Mints / lists / looks up / revokes PUBLIC, token-gated share links for a
 * SINGLE card-workspace file. The returned `url` is the unguessable public
 * capability (`/p/f/:token`) — anyone with it can view/download that one file.
 *
 * Gating: every consumer is behind env.VITE_EXTERNAL_LINKS; the gateway routes
 * are account-owner / org-admin gated server-side, so calls degrade gracefully
 * on a 403/404 (caller not an owner / older gateway) via ApiError.
 */
import { apiJson } from './client';

// ── shapes ─────────────────────────────────────────────────────────────

/** The minimal link payload returned by create / lookup. */
export interface ExternalLink {
  id: string;
  token: string;
  /** Public capability URL: `${origin}/p/f/${token}`. */
  url: string;
  filename: string;
  createdAt: string;
  expiresAt: string | null;
}

/** A nested {id,name} ref the management list groups by. */
export interface LinkOrgRef {
  id: string;
  name: string | null;
}
export interface LinkCardRef {
  id: string;
  title: string | null;
}

/** One row of the account-wide management list (GET /api/external-links). */
export interface ExternalLinkRow {
  id: string;
  token: string;
  url: string;
  filename: string;
  /** Canonical /srv/chaos/.../workspace/... path. */
  path: string;
  createdAt: string;
  accessCount: number;
  expiresAt: string | null;
  org: LinkOrgRef | null;
  project: LinkOrgRef | null;
  board: LinkOrgRef | null;
  card: LinkCardRef | null;
}

interface CreateResp {
  id: string;
  token: string;
  url: string;
  filename: string;
  createdAt: string;
  expiresAt: string | null;
}
interface ListResp {
  links: ExternalLinkRow[];
}
interface LookupResp {
  link: ExternalLink | null;
}
interface RevokeResp {
  ok: boolean;
}
interface BulkRevokeResp {
  revoked: number;
}

// ── helpers ──────────────────────────────────────────────────────────────

function pickLink(r: CreateResp): ExternalLink {
  return {
    id: r.id,
    token: r.token,
    url: r.url,
    filename: r.filename,
    createdAt: r.createdAt,
    expiresAt: r.expiresAt,
  };
}

// ── api ────────────────────────────────────────────────────────────────

export const externalLinksApi = {
  /**
   * Create-or-get an active link for a canonical file path. Idempotent: the
   * gateway returns the existing active link for the path if one exists.
   * `expiresInDays` null/0 = never expires.
   */
  createForPath(path: string, expiresInDays: number | null = null): Promise<ExternalLink> {
    return apiJson<CreateResp>('/external-links', {
      method: 'POST',
      json: { path, expiresInDays: expiresInDays ?? undefined },
    }).then(pickLink);
  },

  /**
   * Create-or-get a link via the card + filename convenience route — the
   * gateway derives the canonical path from the card's stored workspace path,
   * so the client never has to know the full /srv/chaos path.
   */
  createForCardFile(
    cardId: string,
    filename: string,
    expiresInDays: number | null = null,
  ): Promise<ExternalLink> {
    return apiJson<CreateResp>(
      `/cards/${cardId}/files/${encodeURIComponent(filename)}/link`,
      { method: 'POST', json: { expiresInDays: expiresInDays ?? undefined } },
    ).then(pickLink);
  },

  /** Fetch the active link for a canonical path, or null. */
  getForPath(path: string): Promise<ExternalLink | null> {
    const q = new URLSearchParams({ path });
    return apiJson<LookupResp>(`/external-links/by-path?${q.toString()}`).then((r) => r.link);
  },

  /** Fetch the active link for a card file, or null. */
  getForCardFile(cardId: string, filename: string): Promise<ExternalLink | null> {
    return apiJson<LookupResp>(
      `/cards/${cardId}/files/${encodeURIComponent(filename)}/link`,
    ).then((r) => r.link);
  },

  /** Account-wide list of active links (account-owner only; 403 otherwise). */
  list(): Promise<ExternalLinkRow[]> {
    return apiJson<ListResp>('/external-links').then((r) => r.links);
  },

  /** Revoke one link by id (account-owner OR org-admin for the link's org). */
  revoke(id: string): Promise<RevokeResp> {
    return apiJson<RevokeResp>(`/external-links/${id}`, { method: 'DELETE' });
  },

  /**
   * Bulk revoke (account-owner only). Exactly one selector:
   *   { all: true }            — every active link across the account
   *   { orgId }                — every active link of one organization
   *   { path }                 — every active link for one canonical path
   * Returns the number revoked.
   */
  revokeBulk(sel: { all?: boolean; orgId?: string; path?: string }): Promise<number> {
    const q = new URLSearchParams();
    if (sel.all) q.set('scope', 'all');
    else if (sel.orgId) q.set('orgId', sel.orgId);
    else if (sel.path) q.set('path', sel.path);
    return apiJson<BulkRevokeResp>(`/external-links?${q.toString()}`, {
      method: 'DELETE',
    }).then((r) => r.revoked);
  },
};

export const externalLinksKeys = {
  all: ['external-links'] as const,
  /** Account-wide management list. */
  list: () => [...externalLinksKeys.all, 'list'] as const,
  /** Active link for a canonical path. */
  byPath: (path: string) => [...externalLinksKeys.all, 'by-path', path] as const,
  /** Active link for a card file. */
  byCardFile: (cardId: string, filename: string) =>
    [...externalLinksKeys.all, 'by-card-file', cardId, filename] as const,
};
